/**
 * Pieces shared by the two account switchers: the desktop one in the LeftBar
 * (components/Layout/LeftBar/AccountSwitcher.tsx) and the mobile one in the
 * tab bar's "…" menu (components/MobileTabBar/MobileTabBar.tsx).
 *
 * Kept in one place deliberately — a hand-rolled copy of Avatar/RoleBadge
 * drifts on font and spacing.
 */
import styled from "@emotion/styled";

import AccountStore from "@stores/AccountStore";
import { AccountMembership } from "@services/teamService";
import { webMemberships } from "@src/utils/teamAccess";

export const getInitials = (name: string): string => {
  return name
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .substring(0, 2);
};

export const Avatar = styled.div<{ size?: number; muted?: boolean }>`
  width: ${({ size = 20 }) => size}px;
  height: ${({ size = 20 }) => size}px;
  border-radius: 50%;
  background: ${({ muted }) => (muted ? "#c3c8ca" : "#68ad18")};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-family: Nunito Sans, sans-serif;
  font-weight: bold;
  font-size: ${({ size = 20 }) => Math.floor(size * 0.5)}px;
  min-width: ${({ size = 20 }) => size}px;
  text-transform: uppercase;
  vertical-align: bottom;
`;

export const RoleBadge = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 10px;
  font-weight: bold;
  color: #68ad18;
  background: #eef6e3;
  border-radius: 4px;
  padding: 1px 6px;
  text-transform: uppercase;
`;

/**
 * Always guarantee a "my account" entry in the list, even if the accounts call
 * failed (e.g. transient network/CORS error) — otherwise a user who switched
 * away could be trapped with no way back to their own account.
 *
 * Accounts whose role is "Driver app only" are never offered on the web (the
 * store already drops them; filtered again here so no switcher can list one).
 */
export function membershipsWithSelf(store: AccountStore): AccountMembership[] {
  const self = store.account;
  const memberships = webMemberships(store.memberships);
  if (!self) return memberships;

  return memberships.some((m) => m.accountId === self.id)
    ? memberships
    : [
        {
          accountId: self.id,
          name: self.name,
          email: self.email,
          role: "owner" as const,
          isSelf: true,
          subscriptionPaid: self.subscriptionPaid,
          cameraInstalled: self.cameraInstalled,
        },
        ...memberships,
      ];
}

/**
 * Show the switcher when there's more than one account, or whenever the user is
 * viewing someone else's account (so they can always return to their own).
 */
export function canSwitchAccounts(store: AccountStore): boolean {
  if (!store.account) return false;
  return membershipsWithSelf(store).length > 1 || store.isMember;
}
